import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { GamePickerWrapper, DateWrapper, GamePickerMiniContainer, DatePickerOverride, DateLabelWrapper } from './GamePicker.style';
import { withTheme } from 'styled-components';
import DatePicker from '@mlb/react-patterns/v4/components/DatePicker/datePicker';
import MiniScoreboard from 'bdata-react-mini-scoreboard-refresh/dist/minisb.bundle';
import MlbMomentTimezoneWrapper from 'mlb-moment-timezone-wrapper/dist/index.bundle.min';
import Button from '@mlb/react-patterns/v4/components/Button/button';
import { ButtonShapes, ButtonStyles } from '@mlb/react-patterns/v4/components/Button/button.types';
import useTracking from '@gameday/utils/hooks/useTracking';
import add from 'date-fns/add';
import sub from 'date-fns/sub';
import isBefore from 'date-fns/isBefore';
import isAfter from 'date-fns/isAfter';
import { format } from 'date-fns';
import { getDateFormatOptions, getDateFormat, DATE_FORMATS } from '@gameday/utils/languageHelpers';
import { ThemeProvider } from '@mlb/react-patterns/components/ThemeProvider/ThemeProvider.tsx';

const GamePicker = ({
    date,
    gamePk,
    language = 'en',
    maxDate,
    minDate,
    gameChangeHandler,
    sportId = [1],
    gameCardMode = 'gameday',
    theme = 'light',
    env,
    localTimezone,
    leagues,
    dictionary
}) => {
    const miniMount = useRef(null);
    const [localDate, setLocalDate] = useState(date);
    const miniInstance = useRef(null);
    const Tracking = useTracking();
    const prefix = (gameCardMode === 'vertical') ? gameCardMode + ' ' : '';
    const options = getDateFormatOptions(language);

    const dateFormat = getDateFormat(language, DATE_FORMATS.DAY);
    // Handlers to call when a game is clicked
    const changeGame = useCallback((gamePk, date, e) => {
        if (gameChangeHandler) {
            gameChangeHandler(gamePk, date);

            const cfg = {
                componentName: [prefix + 'Scoreboard'],
                selectedIndex: e?.currentTarget?.dataset?.index,
                activity: 'Game Card Click',
                text: e?.currentTarget?.dataset?.tracking
            };

            Tracking.track.track_click(cfg);
        }
    }, [gameChangeHandler]);

    // Hide or show the Today button if it's compatible with the current date range
    const showTodayButton = useMemo(() => {
        const today = new Date();
        const todayString = today.toISOString().split('T')[0];
        if (!minDate || isAfter(today, new Date(minDate)) || minDate === todayString) {
            if (!maxDate || isBefore(today, new Date(maxDate)) || maxDate === todayString) {
                return true;
            }
        }
        return false;
    }, [minDate, maxDate]);

    // Change set date if updated from app
    useEffect(() => {
        if (date && !miniInstance.current) {
            miniInstance.current = (new MiniScoreboard({
                mount: miniMount.current,
                route: '/',
                momentTz: new MlbMomentTimezoneWrapper('en', 'America/New_York'),
                gameCardMode,
                hideDatePicker: true,
                activeDate: localDate,
                startDate: localDate,
                endDate: localDate,
                hideScoreboardOnEmpty: false,
                sportIds: sportId,
                leagues,
                locale: language,
                activeGamePk: gamePk,
                clickHandler: (gamePk, date, e) => {
                    changeGame(gamePk, date, e);
                },
                gamePk,
                darkMode: theme === 'dark',
                env,
                localTimezone,
                baseColor: 'var(--gameday-basepath-color)',
                outColor: 'var(--gameday-outs-color)',
                dictionary

            }));
            if (miniMount.current) {
                miniInstance.current.render();
            }
        }
    }, [date]);

    const datePickerHandlers = useMemo(() => ({
        onChange: (d) => {
            const formattedDate = format(d, 'yyyy-MM-dd');
            // const formattedDate = d.toISOString().split('T')[0];
            miniInstance.current?.dispatchDateSwitch(formattedDate);
            setLocalDate(formattedDate);
        }
    }), []);

    const nextDateHandler = useMemo(() => ({
        onClick: () => {
            if (!maxDate || isAfter(new Date(maxDate), new Date(localDate))) {
                const nextDate = add(new Date(localDate), { days: 1 });
                const newLocalDate = nextDate.toISOString().split('T')[0];
                miniInstance.current?.dispatchDateSwitch(newLocalDate);
                setLocalDate(newLocalDate);
                const cfg = {
                    componentName: ['Game Picker'],
                    text: 'Next',
                    activity: 'Button Click'
                };
                Tracking.track.track_click(cfg);
            }
        }
    }), [maxDate, localDate]);

    const prevDateHandler = useMemo(() => ({
        onClick: () => {
            if (!minDate || isBefore(new Date(minDate), new Date(localDate))) {
                const nextDate = sub(new Date(localDate), { days: 1 });
                const newLocalDate = nextDate.toISOString().split('T')[0];
                miniInstance.current?.dispatchDateSwitch(newLocalDate);
                setLocalDate(newLocalDate);
                const cfg = {
                    componentName: ['Game Picker'],
                    text: 'Previous',
                    activity: 'Button Click'
                };
                Tracking.track.track_click(cfg);
            }
        }
    }), [minDate, localDate]);

    useEffect(() => {
        miniInstance.current?.dispatchDateSwitch(date);
    }, [date]);
    const datePickerTheme = useMemo(() => {
        return { dark: theme === 'dark', type: 'auto' };
    }, [theme]);

    const [year, month, day] = localDate.split('-');
    const selectedDate = new Date(year, month - 1, day);
    const isVerticalGame = gameCardMode === 'vertical';

    const DateComponent = useMemo(() => {
        const formattedDate = format(selectedDate, dateFormat, options);
        return ({ displayDatePicker = true }) =>
            <>
                <Button
                    iconName='carousel-arrow-left'
                    styleName={ButtonStyles.DEFAULT}
                    shape={ButtonShapes.HOLLOW}
                    handlers={prevDateHandler}
                    disabled={localDate === minDate}
                />
                {displayDatePicker
                    ? <ThemeProvider theme={datePickerTheme}>
                        <DatePicker
                            showDateText
                            lang={language}
                            dateTextFormatOverride={language === 'es' ? 'dd MMM' : 'MMM dd'}
                            isMobile={false} // This component is DESKTOP-ONLY for right now
                            selectedDate={selectedDate}
                            handlers={datePickerHandlers}
                            yearPickerEnabled={true}
                            showTodayButton={showTodayButton}
                        />
                    </ThemeProvider>
                    : <DateLabelWrapper>{formattedDate}</DateLabelWrapper>}
                <Button
                    iconName='carousel-arrow-right'
                    styleName={ButtonStyles.DEFAULT}
                    shape={ButtonShapes.HOLLOW}
                    handlers={nextDateHandler}
                    disabled={localDate === maxDate}
                />
            </>;
    }, [selectedDate, language, localDate, showTodayButton, dateFormat]);

    return (
        <>
            {!isVerticalGame
                ? <GamePickerWrapper>
                    <DatePickerOverride>
                        <DateComponent/>
                    </DatePickerOverride>
                    <GamePickerMiniContainer ref={miniMount} gameCardMode={gameCardMode} />
                </GamePickerWrapper>
                : <>
                    <DateWrapper>
                        <DateComponent displayDatePicker={false}/>
                    </DateWrapper>
                    <GamePickerMiniContainer ref={miniMount} gameCardMode={gameCardMode} />
                </>
            }
        </>
    );
};

GamePicker.propTypes = {
    gameChangeHandler: PropTypes.func,
    gamePk: PropTypes.number,
    date: PropTypes.string,
    maxDate: PropTypes.string,
    minDate: PropTypes.string,
    sportId: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    ),
    gameCardMode: PropTypes.string,
    language: PropTypes.string,
    theme: PropTypes.string,
    localTimezone: PropTypes.string,
    dictionary: PropTypes.object
};

export default withTheme(memo(GamePicker));
